<template>
  <VApp>
    <VBtn
      variant="text"
      href="#main-content"
      class="skip-link"
      @click.prevent="focusMainContent"
    >
      {{ t('skipToMainContent') }}
    </VBtn>
    <ErrorBoundary>
      <Component
        :is="layouts[layout]"
        :options="layoutOptions"
      >
        <RouterView :key="route.path" />
      </Component>
      <AppToasts />
    </ErrorBoundary>
    <VueQueryDevtools

      v-if="env !== 'production'"
      button-position="bottom-left"
    />
  </VApp>
</template>

<script setup lang="ts">
import { VueQueryDevtools } from '@tanstack/vue-query-devtools';
import {
  ModuleRegistry,
  ClientSideRowModelModule,
  enableDevValidations,
  ClientSideRowModelApiModule,
  ExternalFilterModule,
  CheckboxEditorModule,
  TooltipModule,
  NumberEditorModule,
  CellStyleModule,
  RowStyleModule,
  QuickFilterModule,
  RenderApiModule,
  PaginationModule,
  ColumnAutoSizeModule,
  TextEditorModule,
  TextFilterModule,
  ColumnApiModule,
  RowSelectionModule,
  CustomEditorModule,
  CsvExportModule,
} from 'ag-grid-community';
import { RowGroupingModule, RichSelectModule, SideBarModule, ColumnsToolPanelModule } from 'ag-grid-enterprise';
import { onBeforeMount, onBeforeUnmount } from 'vue';
import { useHead } from '@unhead/vue';
import { getLocalStorageItem, STORAGE_KEYS } from '@/utils/localStorage';
import { useLayouts } from '@/composables/useLayouts';
import { useLocales } from '@/composables/useLocales';
import { getRouteTitle } from '@/composables/useTitleTag';
import { AppToasts } from '@teamworksdev/vuetify';
import { initGainsight, resetGainsight } from '@/plugins/gainsight';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import ErrorBoundary from '@/components/common/ErrorBoundary.vue';


const env = import.meta.env.VITE_ENV;

const { t } = useI18n();
const { layout, layouts, layoutOptions } = useLayouts();
const { setLocale } = useLocales();
const routeTitle = getRouteTitle();
const route = useRoute();

const focusMainContent = () => {
  document.getElementById('main-content')?.focus();
};

useHead({
  templateParams: {
    site: { name: 'General Manager' },
    separator: '|',
  },
  titleTemplate: '%s %separator %site.name',
  title: () => routeTitle.value,
});

onBeforeMount(() => {
  setLocale(
    getLocalStorageItem<string>(STORAGE_KEYS.APP_LOCALE)
      || window.navigator.language,
  );
  initGainsight();
  if (env !== 'production') {
    enableDevValidations();
  }
  // Register ag-grid modules
  ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    RowGroupingModule,
    ClientSideRowModelApiModule,
    ExternalFilterModule,
    CheckboxEditorModule,
    TooltipModule,
    NumberEditorModule,
    CellStyleModule,
    RowStyleModule,
    QuickFilterModule,
    RenderApiModule,
    PaginationModule,
    ColumnAutoSizeModule,
    TextEditorModule,
    TextFilterModule,
    ColumnApiModule,
    RowSelectionModule,
    CustomEditorModule,
    CsvExportModule,
    RichSelectModule,
    SideBarModule,
    ColumnsToolPanelModule,
  ]);
});

onBeforeUnmount(() => {
  resetGainsight();
});




</script>

<style scoped lang="scss">
.skip-link {
  position: fixed;
  top: 0;
  left: 50%;
  z-index: 9999;
  transform: translate(-50%, -100%);

  &:focus {
    transform: translate(-50%, 1rem);
  }
}
</style>
